import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Tooltip,
  useTheme,
  CircularProgress,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CategoryOutlined as CategoryIcon,
  FolderOpen as FolderOpenIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import categoryService from '../../services/categoryService';
import { getCurrentUser } from '../../services/authService';
import PageTitle from '../../components/PageTitle';

/**
 * Categories management page component
 * @returns {JSX.Element} Categories page
 */
const CategoriesPage = () => {
  const theme = useTheme();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development
  
  // State management
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    parentCategoryId: '',
    imageFile: null,
    imagePreview: '',
    imageUrl: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [imageError, setImageError] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'All Categories' }]);
  
  // Memoize loadCategories function
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      
      if (currentCategory) {
        data = await categoryService.getSubcategories(currentCategory.id);
        setCategories(data);
      } else {
        const response = await categoryService.getAllCategories({
          page,
          size: rowsPerPage,
          sort: 'name',
          direction: 'ASC'
        });
        setCategories(response.content);
        setTotalElements(response.totalElements);
      }
      
      const allCategories = await categoryService.getCategoriesByCompany(companyId);
      setParentCategories(allCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load categories',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, currentCategory, companyId]);
  
  // Initial load
  useEffect(() => {
    loadCategories();
  }, [loadCategories]); // loadCategories is now memoized
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Dialog handlers
  const handleOpenDialog = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setFormValues({
        name: category.name,
        description: category.description || '',
        parentCategoryId: category.parentCategory?.id || '',
        imageFile: null,
        imagePreview: category.imageUrl ? categoryService.getCategoryImageUrl(category.imageUrl) : '',
        imageUrl: category.imageUrl || ''
      });
    } else {
      setSelectedCategory(null);
      setFormValues({
        name: '',
        description: '',
        parentCategoryId: currentCategory ? currentCategory.id : '',
        imageFile: null,
        imagePreview: '',
        imageUrl: ''
      });
    }
    setImageError('');
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError('');

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      const msg = 'Unsupported format. Please upload JPEG or PNG.';
      setImageError(msg);
      setSnackbar({
        open: true,
        message: 'Please upload a valid image file (JPEG or PNG)',
        severity: 'error'
      });
      return;
    }

    // Validate file size (1KB - 500KB)
    const minSize = 1 * 1024; // 1KB
    const maxSize = 500 * 1024; // 500KB
    if (file.size < minSize || file.size > maxSize) {
      const msg = file.size < minSize
        ? 'Image is smaller than 1KB. Please upload 1KB–500KB.'
        : 'Image exceeds 500KB. Please upload 1KB–500KB.';
      setImageError(msg);
      setSnackbar({
        open: true,
        message: 'Image size should be between 1KB and 500KB',
        severity: 'error'
      });
      return;
    }

    // Preview the image
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormValues(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result
      }));
    };
    reader.readAsDataURL(file);

    // Upload the image
    try {
      setUploadLoading(true);
      const imageUrl = await categoryService.uploadCategoryImage(file);
      console.log('Image uploaded successfully:', imageUrl);
      setFormValues(prev => ({
        ...prev,
        imageUrl
      }));
      setImageError('');
    } catch (error) {
      console.error('Error uploading image:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload image. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Handle removing the image
  const handleRemoveImage = () => {
    setFormValues(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: '',
      imageUrl: ''
    }));
  };
  
  // Form submission
  const handleSubmit = async () => {
    try {
      const categoryData = {
        name: formValues.name,
        description: formValues.description,
        parentCategoryId: formValues.parentCategoryId || null,
        companyId,
        imageUrl: formValues.imageUrl
      };
      
      if (selectedCategory) {
        // Update existing category
        await categoryService.updateCategory(selectedCategory.id, categoryData);
        setSnackbar({
          open: true,
          message: 'Category updated successfully',
          severity: 'success'
        });
      } else {
        // Create new category
        await categoryService.createCategory(categoryData);
        setSnackbar({
          open: true,
          message: 'Category created successfully',
          severity: 'success'
        });
      }
      
      // Close the dialog and refresh data
      handleCloseDialog();
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${selectedCategory ? 'update' : 'create'} category: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Delete handlers
  const handleOpenDeleteDialog = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };
  
  const handleDeleteCategory = async () => {
    try {
      await categoryService.deleteCategory(categoryToDelete.id);
      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete category: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Handle navigation to subcategories
  const handleNavigateToSubcategories = (category) => {
    setCurrentCategory(category);
    setBreadcrumbs([...breadcrumbs, { id: category.id, name: category.name }]);
  };
  
  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (breadcrumb, index) => {
    if (index === 0) {
      // Back to root level
      setCurrentCategory(null);
      setBreadcrumbs([{ id: null, name: 'All Categories' }]);
    } else {
      // Navigate to specific breadcrumb
      const category = breadcrumbs[index];
      setCurrentCategory({ id: category.id, name: category.name });
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    }
  };
  
  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  return (
    <Layout>
      <PageTitle title="Categories" />
      <Box sx={{ width: '100%', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Product Categories
            </Typography>
            
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
              {breadcrumbs.map((breadcrumb, index) => (
                <Link
                  key={index}
                  color="inherit"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onClick={() => handleBreadcrumbClick(breadcrumb, index)}
                >
                  {index === 0 ? (
                    <FolderOpenIcon sx={{ mr: 0.5 }} fontSize="small" />
                  ) : (
                    <CategoryIcon sx={{ mr: 0.5 }} fontSize="small" />
                  )}
                  {breadcrumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Category
          </Button>
        </Box>
        
        {/* Current location */}
        {currentCategory && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                const newBreadcrumbs = [...breadcrumbs];
                newBreadcrumbs.pop();
                setBreadcrumbs(newBreadcrumbs);
                const parentCategory = newBreadcrumbs.length > 1 ? 
                  { id: newBreadcrumbs[newBreadcrumbs.length - 1].id, name: newBreadcrumbs[newBreadcrumbs.length - 1].name } : 
                  null;
                setCurrentCategory(parentCategory);
              }}
            >
              Back
            </Button>
            <Typography variant="h6" sx={{ ml: 2 }}>
              Viewing subcategories of: <strong>{currentCategory.name}</strong>
            </Typography>
          </Box>
        )}
        
        {/* Categories Table */}
        <Paper elevation={0} sx={{ 
          p: 0, 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Parent Category</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {category.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {category.imageUrl ? (
                          <Box
                            component="img"
                            src={categoryService.getCategoryImageUrl(category.imageUrl)}
                            alt={category.name}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'contain',
                              borderRadius: 1
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'action.hover',
                              borderRadius: 1
                            }}
                          >
                            <ImageIcon sx={{ color: 'text.secondary' }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{category.description || '-'}</TableCell>
                      <TableCell>
                        {category.parentCategory ? (
                          <Chip
                            label={category.parentCategory.name}
                            size="small"
                            icon={<CategoryIcon />}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="View Subcategories">
                            <IconButton
                              color="primary"
                              onClick={() => handleNavigateToSubcategories(category)}
                            >
                              <FolderOpenIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              color="info"
                              onClick={() => handleOpenDialog(category)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleOpenDeleteDialog(category)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination - only show when not viewing subcategories */}
          {!currentCategory && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
      
      {/* Add/Edit Category Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Category Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              margin="normal"
              required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon />
                      </InputAdornment>
                    ),
                  }}
            />
            
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formValues.description}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Parent Category</InputLabel>
              <Select
                name="parentCategoryId"
                value={formValues.parentCategoryId}
                onChange={handleInputChange}
                label="Parent Category"
              >
                <MenuItem value="">
                  <em>None (Root Category)</em>
                </MenuItem>
                {parentCategories.map((category) => (
                  // Don't allow setting own ID as parent (would create circular reference)
                  selectedCategory?.id !== category.id && (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  )
                ))}
              </Select>
            </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Category Image
                </Typography>
                
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  {formValues.imagePreview ? (
                    <Box
                      component="img"
                      src={formValues.imagePreview}
                      alt="Category preview"
                      sx={{
                        width: '100%',
                        height: 150,
                        objectFit: 'contain',
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 150,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'action.hover',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={loading || uploadLoading}
                      startIcon={uploadLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                      sx={{ flexGrow: 1 }}
                    >
                      {uploadLoading ? 'Uploading...' : 'Upload Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </Button>
                    {formValues.imagePreview && (
                      <IconButton 
                        onClick={handleRemoveImage}
                        color="error"
                        disabled={loading || uploadLoading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    Max size: 1KB - 500KB • Formats: JPEG, PNG
                  </Typography>
                  {imageError && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, textAlign: 'center' }}>
                      {imageError}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formValues.name || uploadLoading}
          >
            {selectedCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category <strong>{categoryToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
            Note: Categories with subcategories cannot be deleted. You must delete or reassign subcategories first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            onClick={handleDeleteCategory} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default CategoriesPage; 
